import { apiGet, apiPost, apiPublicGet, API_BASE_URL } from './api';

/**
 * Fetch statistics summary for admin dashboard
 */
export function getDashboardStats(params = {}) {
  const query = new URLSearchParams(params).toString();
  return apiGet(`/admin/dashboard-stats.php${query ? '?' + query : ''}`);
}

/**
 * Fetch all academic courses (Admin protected)
 */
export function getCourses(params = {}) {
  const query = new URLSearchParams(params).toString();
  return apiGet(`/admin/get-courses.php${query ? '?' + query : ''}`);
}

/**
 * Fetch public list of active academic courses (Public read-only)
 */
export function getPublicCourses() {
  return apiPublicGet('/public/courses.php');
}

/**
 * Create a new academic course
 */
export function createCourse(data) {
  return apiPost('/admin/create-course.php', data);
}

/**
 * Update an existing academic course details
 */
export function updateCourse(id, data) {
  return apiPost('/admin/update-course.php', { ...data, id });
}

/**
 * Delete/deactivate an academic course
 */
export function deleteCourse(id) {
  return apiPost('/admin/delete-course.php', { id });
}

/**
 * Fetch all batches
 */
export function getBatches(params = {}) {
  const query = new URLSearchParams(params).toString();
  return apiGet(`/admin/batches${query ? '?' + query : ''}`);
}

/**
 * Create a new batch under a course
 */
export function createBatch(data) {
  return apiPost('/admin/create-batch.php', data);
}

/**
 * Update an existing batch
 */
export function updateBatch(id, data) {
  return apiPost('/admin/update-batch.php', { ...data, id });
}

/**
 * Delete a batch
 */
export function deleteBatch(id) {
  return apiPost('/admin/delete-batch.php', { id });
}

/**
 * Fetch all enrolled students
 */
export function getStudents(params = {}) {
  const query = new URLSearchParams(params).toString();
  return apiGet(`/admin/get-students.php${query ? '?' + query : ''}`);
}

/**
 * Enroll/admit a student
 */
export function createStudent(data) {
  return apiPost('/admin/create-student.php', data);
}

/**
 * Update student account settings
 */
export function updateStudent(id, data) {
  return apiPost('/admin/update-student.php', { ...data, id });
}

/**
 * Delete a student account
 */
export function deleteStudent(id) {
  return apiPost('/admin/delete-student.php', { id });
}

/**
 * Assign a student to a batch cohort
 */
export function assignStudentBatch(data) {
  return apiPost('/admin/assign-student-batch.php', data);
}

/**
 * Assign a student to a specific course & upcoming batch cohort
 */
export function assignStudentCourseBatch(data) {
  return apiPost('/admin/assign-student-course-batch.php', data);
}

/**
 * Fetch all faculty / employees
 */
export function getEmployees(params = {}) {
  const query = new URLSearchParams(params).toString();
  return apiGet(`/admin/employees${query ? '?' + query : ''}`);
}

/**
 * Create a new faculty/employee profile
 */
export function createEmployee(data) {
  return apiPost('/admin/create-employee.php', data);
}

/**
 * Update employee profile details
 */
export function updateEmployee(id, data) {
  return apiPost('/admin/update-employee.php', { ...data, id });
}

/**
 * Delete an employee profile
 */
export function deleteEmployee(id) {
  return apiPost('/admin/delete-employee.php', { id });
}

/**
 * Assign a list of batches to an employee
 */
export function assignEmployeeBatch(data) {
  return apiPost('/admin/assign-employee-batch.php', data);
}

/**
 * Fetch all classroom lectures
 */
export function getLectures(batchId, params = {}) {
  const query = new URLSearchParams({ ...params, batch_id: batchId }).toString();
  return apiGet(`/admin/batch-lectures?${query}`);
}

/**
 * Upload a daily lecture video
 */
export function addLecture(data) {
  return apiPost('/admin/add-lecture.php', data);
}

/**
 * Update an uploaded lecture video
 */
export function updateLecture(id, data) {
  return apiPost('/admin/update-lecture.php', { ...data, id });
}

/**
 * Delete a lecture video
 */
export function deleteLecture(id) {
  return apiPost('/admin/delete-lecture.php', { id });
}

/**
 * Fetch all common practice courses
 */
export function getCommonCourses(params = {}) {
  const query = new URLSearchParams(params).toString();
  return apiGet(`/admin/get-common-courses.php${query ? '?' + query : ''}`);
}

/**
 * Create a new common practice course
 */
export function createCommonCourse(data) {
  return apiPost('/admin/create-common-course.php', data);
}

/**
 * Update a common practice course
 */
export function updateCommonCourse(id, data) {
  return apiPost('/admin/update-common-course.php', { ...data, id });
}

/**
 * Delete a common practice course
 */
export function deleteCommonCourse(id) {
  return apiPost('/admin/delete-common-course.php', { id });
}

/**
 * Add a video to a common practice course
 */
export function addCommonVideo(data) {
  return apiPost('/admin/add-common-video.php', data);
}

/**
 * Update a common practice video details
 */
export function updateCommonVideo(id, data) {
  return apiPost('/admin/update-common-video.php', { ...data, id });
}

/**
 * Delete a video from a common practice course
 */
export function deleteCommonVideo(id) {
  return apiPost('/admin/delete-common-video.php', { id });
}

/**
 * Fetch employee details (single teacher context)
 */
export function getEmployeeDetails(id) {
  return apiGet(`/admin/get-employee-details.php?id=${id}`);
}

/**
 * Fetch detailed teacher profile details for admin view
 */
export function getTeacherProfileForAdmin(id) {
  return apiGet(`/student/get-teacher-profile.php?teacher_id=${id}`);
}

/**
 * Update detailed teacher profile details by admin
 */
export function updateTeacherProfileForAdmin(id, data) {
  return apiPost('/admin/update-employee-profile.php', { ...data, id });
}

/**
 * Remove a single batch assignment from a teacher
 */
export function removeEmployeeBatch(employee_id, batch_id) {
  return apiPost('/admin/remove-employee-batch.php', { employee_id, batch_id });
}

/**
 * Fetch paginated and filtered staff activities logs
 */
export function getStaffActivities(params = {}) {
  const query = new URLSearchParams(params).toString();
  return apiGet(`/admin/get-staff-activities.php${query ? '?' + query : ''}`);
}

/**
 * Fetch detailed profile and enrollment data for a student
 */
export function getStudentDetails(id) {
  return apiGet(`/admin/get-student-details.php?id=${id}`);
}

/**
 * Record a student fee payment
 */
export function addFeePayment(data) {
  return apiPost('/admin/add-fee-payment.php', data);
}

/**
 * Fetch teacher submitted lectures for review
 */
export function getReviewLectures(params = {}) {
  const query = new URLSearchParams(params).toString();
  return apiGet(`/admin/review-lectures${query ? '?' + query : ''}`);
}

/**
 * Perform action (approve/correction/reject) on a submitted lecture
 */
export function reviewLectureAction(lectureId, action, feedback = '') {
  return apiPost('/admin/review-lectures/action', { id: lectureId, action, feedback });
}

/**
 * Fetch counts of pending reviews (lectures, questions, exams) for admin sidebar badge
 */
export function getPendingReviewCounts() {
  return apiGet('/admin/review-counts');
}

/**
 * Fetch all coupon codes with optional filters
 */
export function getCoupons(params = {}) {
  const query = new URLSearchParams(params).toString();
  return apiGet(`/admin/get-coupons.php${query ? '?' + query : ''}`);
}

/**
 * Create a new coupon code
 */
export function createCoupon(data) {
  return apiPost('/admin/create-coupon.php', data);
}

/**
 * Update an existing coupon details
 */
export function updateCoupon(id, data) {
  return apiPost('/admin/update-coupon.php', { ...data, id });
}

/**
 * Delete a coupon code
 */
export function deleteCoupon(id) {
  return apiPost('/admin/delete-coupon.php', { id });
}

/**
 * Toggle active/inactive status of a coupon code
 */
export function toggleCouponStatus(id, status) {
  return apiPost('/admin/toggle-coupon.php', { id, status });
}

/**
 * Fetch interested students leads with optional filters
 */
export function getInterestedStudents(params = {}) {
  const query = new URLSearchParams(params).toString();
  return apiGet(`/admin/get-interested-students.php${query ? '?' + query : ''}`);
}

/**
 * Mark all interested students leads as seen
 */
export function markInterestedSeen() {
  return apiPost('/admin/mark-interested-seen.php');
}

/**
 * Fetch unseen counts for admin sidebar notification badges
 */
export function getUnseenCounts() {
  return apiGet('/admin/get-unseen-counts.php');
}

/**
 * Fetch map of student_id to unseen enrollment notifications count
 */
export function getStudentNotificationsCount() {
  return apiGet('/admin/get-student-notifications-count.php');
}

/**
 * Mark enrollment notifications for a specific student as seen
 */
export function markEnrollmentSeen(studentId) {
  return apiPost('/admin/mark-enrollment-seen.php', { student_id: studentId });
}

/**
 * Mark all notifications for a student as seen
 */
export function markNotificationsSeen(studentId) {
  return apiPost('/admin/students/mark-notifications-seen', { student_id: studentId });
}

/**
 * Fetch total unseen student notification count
 */
export function getStudentsNotificationCount() {
  return apiGet('/admin/students/notification-count');
}

/**
 * Fetch list of students with overdue installments
 */
export function getFeeDueList(params = {}) {
  const query = new URLSearchParams(params).toString();
  return apiGet(`/admin/get-fee-due-list.php${query ? '?' + query : ''}`);
}

/**
 * Send overdue reminder to student
 */
export function sendDueReminder(installmentId, method) {
  return apiPost('/admin/send-due-reminder.php', {
    installment_id: installmentId,
    method
  });
}

/**
 * Suspend/unsuspend student course access
 */
export function suspendStudentAccess(enrollmentId, status) {
  return apiPost('/admin/suspend-student-access.php', {
    enrollment_id: enrollmentId,
    status
  });
}

/**
 * Trigger manual scan for overdue installments
 */
export function checkOverdueInstallments() {
  return apiPost('/admin/check-overdue-installments.php');
}

/**
 * Fetch teacher applications list
 */
export function getTeacherApplications(params = {}) {
  const query = new URLSearchParams(params).toString();
  return apiGet(`/admin/get-teacher-applications.php${query ? '?' + query : ''}`);
}

/**
 * Fetch detailed teacher application profile, action history and notifications
 */
export function getTeacherApplicationDetails(id) {
  return apiGet(`/admin/get-teacher-application-details.php?id=${id}`);
}

/**
 * Perform review action (shortlist, approve, reject, need_info, delete) on a teacher application
 */
export function teacherApplicationAction(id, action, reason = '', notifyApplicant = false, password = '', courseIds = []) {
  return apiPost('/admin/teacher-application-action.php', {
    id,
    action,
    reason,
    notify_applicant: notifyApplicant,
    password,
    course_ids: courseIds
  });
}

/**
 * Convert an approved teacher application to a teacher/employee account
 */
export function createTeacherAccount(id, password, expertIn = '', courseIds = []) {
  return apiPost('/admin/create-teacher-account.php', {
    id,
    password,
    expert_in: expertIn,
    course_ids: courseIds
  });
}

/**
 * Submit teacher application (public API called from applicant form)
 */
export function submitTeacherApplication(data) {
  return apiPost('/submit-teacher-application.php', data);
}

/**
 * Save teacher application draft progress
 */
export function saveTeacherApplicationDraft(data) {
  return apiPost('/save-teacher-application-draft.php', data);
}

/**
 * Fetch teacher application draft progress
 */
export function getTeacherApplicationDraft(email) {
  return apiPublicGet(`/get-teacher-application-draft.php?email=${encodeURIComponent(email)}`);
}

/**
 * Mark a teacher application as seen by admin
 */
export function markTeacherApplicationSeen(id) {
  return apiPost('/admin/mark-teacher-application-seen.php', { id });
}

/**
 * Fetch linked teacher application details for an employee ID
 */
export function getEmployeeTeacherApplication(employeeId) {
  return apiGet(`/admin/get-employee-teacher-application.php?id=${employeeId}`);
}

/**
 * Update status of an interested student lead
 */
export function updateInterestedStatus(leadId, status) {
  return apiPost('/admin/update-interested-status.php', { lead_id: leadId, status });
}

/**
 * Delete an interested student lead inquiry record
 */
export function deleteInterestedStudent(leadId) {
  return apiPost('/admin/delete-interested-student.php', { lead_id: leadId });
}

/**
 * Fetch teacher profile update requests for admin review
 */
export function getProfileUpdateRequests(params = {}) {
  const query = new URLSearchParams(params).toString();
  return apiGet(`/admin/get-profile-update-requests.php${query ? '?' + query : ''}`);
}

/**
 * Approve, reject, or delete a teacher profile update request
 */
export function reviewProfileUpdateRequest(id, action, rejectionReason = '') {
  return apiPost('/admin/review-profile-update-request.php', {
    id,
    action,
    rejection_reason: rejectionReason
  });
}

/**
 * Permanently delete a teacher profile update request
 */
export function deleteProfileUpdateRequest(id) {
  return apiPost('/admin/review-profile-update-request.php', {
    id,
    action: 'delete'
  });
}

/**
 * Clear completed (approved or rejected) profile update requests
 */
export function clearOldProfileUpdateRequests() {
  return apiPost('/admin/clear-profile-update-requests.php');
}

/**
 * Fetch paginated and filtered total fee collections list and totals
 */
export function getFeeCollections(params = {}) {
  const query = new URLSearchParams(params).toString();
  return apiGet(`/admin/get-fee-collections.php${query ? '?' + query : ''}`);
}

/**
 * Mark a fee installment as handled
 */
export function markFeeHandled(data) {
  return apiPost('/admin/mark-fee-handled.php', data);
}

/**
 * Send a test WhatsApp template message using Meta Cloud API parameters.
 */
export function sendWhatsAppTestMessage(payload) {
  return apiPost('/admin/send-whatsapp-test', payload);
}

/**
 * Send a campaign notification to an interested student lead
 */
export function sendCampaign(leadId, method, batchStartDate, courseLink) {
  return apiPost('/admin/send-campaign.php', {
    lead_id: leadId,
    method,
    batch_start_date: batchStartDate,
    course_link: courseLink
  });
}

/**
 * Export all students directory as CSV downloadable blob
 */
export async function exportStudentsCSV() {
  const token = localStorage.getItem('token');
  const headers = {
    'X-Requested-With': 'XMLHttpRequest'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const response = await fetch(`${API_BASE_URL}/admin/export-students`, {
    method: 'GET',
    headers,
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to export students details.');
  }
  return response.blob();
}

// Fetch teacher submitted demo/intro videos for admin review
export function getReviewDemoVideos(params = {}) {
  const cleanParams = {};
  Object.keys(params).forEach(key => {
    let val = params[key];
    if (val !== undefined && val !== null && val !== '') {
      if (key === 'status') {
        if (val !== 'all') {
          cleanParams['review_status'] = val;
        }
      } else if (key === 'review_status') {
        if (val !== 'all') {
          cleanParams['review_status'] = val;
        }
      } else {
        cleanParams[key] = val;
      }
    }
  });
  const query = new URLSearchParams(cleanParams).toString();
  return apiGet(`/admin/review-demo-videos${query ? '?' + query : ''}`);
}

// Perform review action (approve/correction/reject/delete) on a submitted demo video
export function reviewDemoVideoAction(videoId, action, feedback = '') {
  return apiPost('/admin/review-demo-videos', { id: videoId, action, feedback });
}

// Request revision on a demo video
export function requestDemoVideoRevision(payload) {
  return apiPost('/admin/review-demo-videos', { ...payload, action: 'request_revision' });
}

// Update demo video details
export function updateDemoVideoDetails(payload) {
  return apiPost('/admin/review-demo-videos', { ...payload, action: 'update_details' });
}

/**
 * Fetch all course mappings for a specific teacher
 */
export function getTeacherCourseMappings(teacherId) {
  return apiGet(`/admin/teacher-course-mappings.php?teacher_id=${teacherId}`);
}

/**
 * Sync (save all) course mappings for a teacher
 */
export function syncTeacherCourseMappings(teacherId, courseIds) {
  return apiPost('/admin/teacher-course-mappings.php', {
    teacher_id: teacherId,
    course_ids: courseIds,
    action: 'sync'
  });
}

/**
 * Remove a single course mapping for a teacher
 */
export function removeTeacherCourseMapping(teacherId, courseId) {
  return apiPost('/admin/teacher-course-mappings.php', {
    teacher_id: teacherId,
    course_id: courseId,
    action: 'remove'
  });
}

/**
 * Fetch active teachers eligible to teach a specific course
 */
export function getEligibleTeachersByCourse(courseId) {
  return apiGet(`/admin/get-eligible-teachers.php?course_id=${courseId}`);
}

/**
 * Create one or more course modules
 */
export function createModules(courseId, modules) {
  return apiPost('/admin/add-module.php', { course_id: courseId, modules });
}

/**
 * Update an existing course module details
 */
export function updateModule(id, courseId, moduleNumber, moduleTitle) {
  return apiPost('/admin/update-module.php', { id, course_id: courseId, module_number: moduleNumber, module_title: moduleTitle });
}

/**
 * Delete a course module
 */
export function deleteModule(id) {
  return apiPost('/admin/delete-module.php', { id });
}

/**
 * Fetch course modules by course_id (returns all if no courseId passed)
 */
export function getCourseModules(courseId = 0) {
  return apiGet(`/admin/get-modules.php${courseId ? '?course_id=' + courseId : ''}`);
}

/**
 * Fetch centre-specific course enquiries with filtering and pagination
 */
export function getCentreCourseEnquiries(params = {}) {
  const query = new URLSearchParams(params).toString();
  return apiGet(`/admin/get-centre-course-enquiries.php${query ? '?' + query : ''}`);
}

/**
 * Soft-delete a centre course enquiry
 */
export function deleteCentreCourseEnquiry(id, reason = '') {
  return apiPost('/admin/delete-centre-course-enquiry.php', { id, reason });
}

